/**
 * GridLayout - Simple CSS Grid layout for photo collections
 *
 * Displays photos in a responsive grid with consistent spacing.
 * Uses container width and zoom to calculate optimal column count.
 *
 * @module PhotoCollection2/layouts/GridLayout
 */

import { useState, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PhotoItem2 } from '../../PhotoItem2';
import { PhotoContextMenu } from '../../PhotoContextMenu';
import { cn } from '@/lib/utils';
import { Text } from '../../atoms/Text';
import { useAuth } from '@/services/auth';
import type { PhotoCollection2LayoutProps, ReorderContext } from '../PhotoCollection2.types';
import type { PhotoItem2ViewModel } from '../../PhotoItem2/PhotoItem2.types';

/**
 * Base target width for images at 100% zoom (for 3:2 landscape aspect ratio)
 */
const BASE_TARGET_WIDTH = 300;

/**
 * Props for SortablePhotoItem wrapper
 */
interface SortablePhotoItemProps {
  photo: PhotoItem2ViewModel;
  itemTemplate: PhotoCollection2LayoutProps['itemTemplate'];
  selectionMode: PhotoCollection2LayoutProps['selectionMode'];
  isSelected: boolean;
  zoom: number;
  onItemClick?: (photoId: string, event?: React.MouseEvent) => void;
  reorderContext?: ReorderContext;
  hasSelection: boolean;
  /** Current user's ID for ownership check (Feature 081: photo-versioning) */
  currentUserId?: string;
}

/**
 * Wrapper component that makes the entire photo item draggable
 *
 * Applies dnd-kit sortable behavior to the wrapper div.
 * Drag is disabled when items are selected to avoid conflict with selection.
 */
function SortablePhotoItem({
  photo,
  itemTemplate,
  selectionMode,
  isSelected,
  zoom,
  onItemClick,
  reorderContext,
  hasSelection,
  currentUserId,
}: SortablePhotoItemProps) {
  const isDragEnabled = reorderContext?.enabled && !hasSelection;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: photo.photoData.id,
    disabled: !isDragEnabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  // Apply drag listeners and cursor styling only when drag is enabled
  const dragProps = isDragEnabled
    ? {
        ...attributes,
        ...listeners,
        style: { ...style, cursor: isDragging ? 'grabbing' : 'grab' },
        'aria-roledescription': 'sortable',
        'aria-describedby': 'dnd-instructions',
      }
    : { style };

  // Feature 081: photo-versioning - Check if current user owns this photo
  const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

  return (
    <div
      ref={setNodeRef}
      {...dragProps}
      className="aspect-square relative"
    >
      <PhotoContextMenu
        photoId={photo.photoData.id}
        isOwner={isOwner}
      >
        <PhotoItem2
          data={photo}
          template={itemTemplate}
          isSelected={selectionMode !== 'none' && isSelected}
          selectable={selectionMode !== 'none'}
          zoom={zoom}
          onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
        />
      </PhotoContextMenu>
    </div>
  );
}

/**
 * Calculate grid columns based on container width and zoom level
 * Target width = BASE_TARGET_WIDTH * zoom (300px at 100% zoom)
 * Columns = floor(containerWidth / targetWidth)
 *
 * @param containerWidth - Width of the container in pixels
 * @param zoom - Zoom multiplier (0.25 = 25%, 1.0 = 100%, 5.0 = 500%)
 * @returns Number of columns (minimum 1)
 */
function calculateGridColumns(containerWidth: number, zoom: number = 1.0): number {
  const targetWidth = BASE_TARGET_WIDTH * zoom;
  const columns = Math.floor(containerWidth / targetWidth);
  return Math.max(1, columns); // Ensure at least 1 column
}

/**
 * GridLayout component
 *
 * Renders photos in a responsive CSS Grid with square tiles.
 * Uses container width and zoom to calculate optimal column count dynamically.
 *
 * Zoom targets specific image widths:
 * - zoom 0.25 (25%): ~75px wide images (high density)
 * - zoom 1.0 (100%): ~300px wide images (default)
 * - zoom 5.0 (500%): ~1500px wide images (maximum zoom)
 *
 * Automatically recalculates columns when container resizes.
 * All tiles are square (1:1 aspect ratio)
 *
 * @example
 * ```typescript
 * <GridLayout
 *   items={photos}
 *   itemTemplate="photo-only"
 *   selectionMode="multiple"
 *   selectedIds={['photo-1']}
 *   zoom={1.0}
 *   onItemClick={handleClick}
 * />
 * ```
 */
export function GridLayout({
  items,
  itemTemplate,
  selectionMode,
  selectedIds,
  zoom = 1.0,
  onItemClick,
  reorderContext,
  className,
}: PhotoCollection2LayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Feature 081: photo-versioning - Get current user for ownership check
  const { user } = useAuth();
  const currentUserId = user?.id;

  // Track container width with ResizeObserver
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // Set initial width
    setContainerWidth(container.offsetWidth);

    // Create ResizeObserver to track width changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);

    return () => {
      // Explicitly unobserve the container to prevent memory leaks
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, []);

  // Calculate columns based on container width and zoom
  const columns = containerWidth > 0 ? calculateGridColumns(containerWidth, zoom) : 4;

  // Check if any items are selected (for disabling drag during selection)
  const hasSelection = selectedIds.length > 0;

  // Empty state
  if (items.length === 0) {
    return (
      <div className={cn('flex items-center justify-center h-64 text-center', className)}>
        <Text variant="muted">No photos in this collection</Text>
      </div>
    );
  }

  // Note: SortableContext is provided by PhotoCollection2, not here
  // This prevents double-wrapping which could cause drag-and-drop issues
  return (
    <div
      ref={containerRef}
      className={cn('grid gap-4 min-h-0', className)}
      style={{
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      }}
    >
      {items.map((photo) => {
        // Feature 081: photo-versioning - Check if current user owns this photo
        const isOwner = currentUserId ? photo.photoData.userId === currentUserId : false;

        return reorderContext?.enabled ? (
          <SortablePhotoItem
            key={photo.photoData.id}
            photo={photo}
            itemTemplate={itemTemplate}
            selectionMode={selectionMode}
            isSelected={selectedIds.includes(photo.photoData.id)}
            zoom={zoom}
            onItemClick={onItemClick}
            reorderContext={reorderContext}
            hasSelection={hasSelection}
            currentUserId={currentUserId}
          />
        ) : (
          <div key={photo.photoData.id} className="aspect-square relative">
            <PhotoContextMenu
              photoId={photo.photoData.id}
              isOwner={isOwner}
            >
              <PhotoItem2
                data={photo}
                template={itemTemplate}
                isSelected={selectionMode !== 'none' && selectedIds.includes(photo.photoData.id)}
                selectable={selectionMode !== 'none'}
                zoom={zoom}
                onClick={(e?: React.MouseEvent) => onItemClick?.(photo.photoData.id, e)}
              />
            </PhotoContextMenu>
          </div>
        );
      })}
    </div>
  );
}
